import React, { useState, useContext } from "react";
import { ref, update } from "firebase/database";
import { signOut } from "firebase/auth";
import { database, auth } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../UserContext";
import { LogIn, LogOut, User } from "lucide-react";

// Import UI components
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const Header = () => {
  const { currentUser } = useContext(UserContext);
  const [isUsernameDialogOpen, setIsUsernameDialogOpen] = useState(false);
  const [newUsername, setNewUsername] = useState("");

  const navigate = useNavigate();

  const handleUsernameChange = () => {
    if (newUsername) {
      const userRef = ref(database, `users/${currentUser.uid}`);
      update(userRef, { displayName: newUsername })
        .then(() => {
          setNewUsername("");
          setIsUsernameDialogOpen(false);
        })
        .catch((error) => {
          console.error("Error updating username:", error);
          alert("Failed to update username. Please try again.");
        });
    }
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        console.log("User signed out");
        navigate("/login");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
        alert("Failed to sign out. Please try again.");
      });
  };

  return (
    <header className="bg-gray-800 shadow-lg">
      <div className="container mx-auto px-4 py-6 flex justify-between items-center">
        <div className="relative cursor-pointer" onClick={() => navigate(currentUser ? "/dashboard" : "/")}>
          <h1 className="text-4xl font-bold text-green-400">
            lisssts
            <span className="absolute -bottom-2 left-0 right-0 h-1 bg-green-400 rounded-full"></span>
          </h1>
        </div>
        {currentUser ? (
          <div className="flex items-center space-x-4">
            <h2
              className="text-gray-300 hover:text-gray-100 m-0 cursor-pointer"
              onClick={() => navigate("/dashboard")}
            >
              Dashboard
            </h2>
            <Dialog
              open={isUsernameDialogOpen}
              onOpenChange={setIsUsernameDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-gray-300 hover:text-gray-100"
                >
                  <User className="h-5 w-5 mr-2" />
                  {currentUser.displayName || currentUser.email}
                </Button>
              </DialogTrigger>

              <DialogContent className="bg-gray-800 text-gray-100">
                <DialogHeader>
                  <DialogTitle className="text-green-400">
                    Change Username
                  </DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="new-username" className="text-right">
                      New Username
                    </Label>
                    <Input
                      id="new-username"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      className="col-span-3 bg-gray-700 text-gray-100 border-gray-600"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleUsernameChange}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Change Username
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-gray-100"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 mr-2" />
              Logout
            </Button>
          </div>
        ) : (
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-gray-100"
              onClick={() => navigate("/login")}
            >
              <LogIn className="h-5 w-5 mr-2" />
              Login
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
